import { useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

/**
 * Modal genérico com overlay, fechamento por Esc e trava de rolagem do body.
 *
 * O tema segue as classes `dark:` do Tailwind, como o restante das páginas
 * autenticadas — os modais mais antigos recebem o tema por prop e não foram
 * migrados aqui.
 */
const Modal = ({ isOpen, title, onClose, children, footer, size, dismissible }) => {
  useEffect(() => {
    if (!isOpen || !dismissible) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, dismissible, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={() => { if (dismissible) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          'w-full rounded-2xl shadow-xl p-6 space-y-4 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100',
          size === 'lg' ? 'max-w-2xl' : 'max-w-md'
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-xl font-semibold">{title}</h2>
        <div className="space-y-3 text-sm">{children}</div>
        {footer && <div className="flex justify-end gap-2 pt-2">{footer}</div>}
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['md', 'lg']),
  dismissible: PropTypes.bool,
};

Modal.defaultProps = {
  children: null,
  footer: null,
  size: 'md',
  dismissible: true,
};

export default Modal;
